const CLAIM_PREFIX = 'nettgrav:mls:welcome-recovery:';
const LOCK_PREFIX = 'nettgrav:mls:welcome-recovery-lock:';
const LEASE_PREFIX = 'nettgrav:mls:welcome-recovery-lease:';

const CLAIM_TTL_MS = 90_000;
const CLAIM_WAIT_TIMEOUT_MS = 30_000;
const CLAIM_POLL_INTERVAL_MS = 100;
const LEASE_TTL_MS = 8_000;

const wait = (delayMs) => new Promise((resolve) => setTimeout(resolve, delayMs));

const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage || null : null;
  } catch {
    return null;
  }
};

const normalizePart = (value, fallback) => {
  const normalized = value == null ? '' : String(value).trim();
  return encodeURIComponent(normalized || fallback);
};

const buildIdentityKey = ({ userId, identityId }) =>
  `${normalizePart(userId, 'anonymous')}:${normalizePart(identityId, 'self')}`;

const buildClaimKey = (identityKey) => `${CLAIM_PREFIX}${identityKey}`;

const readStoredClaim = (claimKey) => {
  try {
    const value = storage()?.getItem(claimKey);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const writeStoredClaim = (claimKey, claim) => {
  try {
    storage()?.setItem(claimKey, JSON.stringify(claim));
  } catch {}
};

const removeStoredClaim = (claimKey, groupId = null) => {
  try {
    const targetStorage = storage();
    const current = readStoredClaim(claimKey);
    if (targetStorage && (!groupId || current?.groupId === groupId)) {
      targetStorage.removeItem(claimKey);
    }
  } catch {}
};

const createToken = () => {
  try {
    return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  } catch {
    return `${Date.now()}-${Math.random()}`;
  }
};

const hasWelcome = (result) => {
  if (!result || typeof result !== 'object') return false;
  if (result.welcome_b64 || result.welcomeB64 || result.pending_welcome || result.pendingWelcome) {
    return true;
  }
  const pending = result.pending_welcomes ?? result.pendingWelcomes;
  return Array.isArray(pending) && pending.length > 0;
};

export class WelcomeRecoveryCoordinator {
  constructor() {
    this.inflight = new Map();
    this.memoryClaims = new Map();
  }

  run({ userId, identityId, groupId, task }) {
    if (typeof task !== 'function') {
      throw new TypeError('Welcome recovery task is required');
    }

    const normalizedGroupId = groupId == null ? '' : String(groupId).trim();
    if (!normalizedGroupId) {
      throw new TypeError('Welcome recovery groupId is required');
    }

    const identityKey = buildIdentityKey({ userId, identityId });
    const operationKey = `${identityKey}:${normalizePart(normalizedGroupId, 'group')}`;
    const existing = this.inflight.get(operationKey);
    if (existing) return existing;

    const pending = this.#runExclusive({
      identityKey,
      groupId: normalizedGroupId,
      task,
    });
    this.inflight.set(operationKey, pending);

    return pending.finally(() => {
      if (this.inflight.get(operationKey) === pending) {
        this.inflight.delete(operationKey);
      }
    });
  }

  settle({ userId, identityId, groupId }) {
    const normalizedGroupId = groupId == null ? '' : String(groupId).trim();
    if (!normalizedGroupId) return false;

    const identityKey = buildIdentityKey({ userId, identityId });
    const claimKey = buildClaimKey(identityKey);
    const current = this.#readClaim({ identityKey, claimKey });
    if (current?.groupId !== normalizedGroupId) return false;

    this.#removeClaim({ identityKey, claimKey, groupId: normalizedGroupId });
    return true;
  }

  reset() {
    this.inflight.clear();
    this.memoryClaims.clear();
    try {
      const targetStorage = storage();
      if (!targetStorage) return;
      const keys = [];
      for (let index = 0; index < targetStorage.length; index += 1) {
        const key = targetStorage.key(index);
        if (key?.startsWith(CLAIM_PREFIX) || key?.startsWith(LEASE_PREFIX)) keys.push(key);
      }
      keys.forEach((key) => targetStorage.removeItem(key));
    } catch {}
  }

  async #runExclusive({ identityKey, groupId, task }) {
    const lockManager = typeof navigator !== 'undefined' ? navigator.locks : null;
    if (lockManager?.request) {
      return lockManager.request(`${LOCK_PREFIX}${identityKey}`, { mode: 'exclusive' }, () =>
        this.#runOwned({ identityKey, groupId, task }),
      );
    }

    if (storage()) {
      return this.#runWithLease({ identityKey, groupId, task });
    }

    return this.#runOwned({ identityKey, groupId, task });
  }

  async #runWithLease({ identityKey, groupId, task }) {
    const leaseKey = `${LEASE_PREFIX}${identityKey}`;
    const token = createToken();
    const deadline = Date.now() + CLAIM_WAIT_TIMEOUT_MS;

    while (Date.now() < deadline) {
      const now = Date.now();
      const current = readStoredClaim(leaseKey);
      if (!current?.token || Number(current.expiresAt || 0) <= now) {
        writeStoredClaim(leaseKey, { token, expiresAt: now + LEASE_TTL_MS });
        if (readStoredClaim(leaseKey)?.token === token) {
          const heartbeat = setInterval(
            () => {
              const active = readStoredClaim(leaseKey);
              if (active?.token === token) {
                writeStoredClaim(leaseKey, {
                  token,
                  expiresAt: Date.now() + LEASE_TTL_MS,
                });
              }
            },
            Math.floor(LEASE_TTL_MS / 3),
          );

          try {
            return await this.#runOwned({ identityKey, groupId, task });
          } finally {
            clearInterval(heartbeat);
            try {
              if (readStoredClaim(leaseKey)?.token === token) {
                storage()?.removeItem(leaseKey);
              }
            } catch {}
          }
        }
      }
      await wait(CLAIM_POLL_INTERVAL_MS);
    }

    return {
      skipped: true,
      coordinated: true,
      reason: 'welcome_recovery_lock_timeout',
      groupId,
    };
  }

  async #runOwned({ identityKey, groupId, task }) {
    const claimKey = buildClaimKey(identityKey);
    const deadline = Date.now() + CLAIM_WAIT_TIMEOUT_MS;

    while (true) {
      const current = this.#readClaim({ identityKey, claimKey });
      if (!current) break;
      if (current.groupId === groupId) {
        return {
          skipped: true,
          coordinated: true,
          phase: current.phase ?? 'provisioning',
          reason: 'welcome_recovery_in_progress',
          groupId,
        };
      }
      if (Date.now() >= deadline) {
        return {
          skipped: true,
          coordinated: true,
          reason: 'welcome_recovery_wait_timeout',
          groupId,
          activeGroupId: current.groupId,
        };
      }
      await wait(CLAIM_POLL_INTERVAL_MS);
    }

    this.#writeClaim({
      identityKey,
      claimKey,
      claim: {
        groupId,
        phase: 'provisioning',
        expiresAt: Date.now() + CLAIM_TTL_MS,
      },
    });

    try {
      const result = await task();
      if (hasWelcome(result)) {
        const current = this.#readClaim({ identityKey, claimKey });
        if (current?.groupId === groupId) {
          this.#writeClaim({
            identityKey,
            claimKey,
            claim: {
              groupId,
              phase: 'awaiting_ack',
              expiresAt: Date.now() + CLAIM_TTL_MS,
            },
          });
        }
      } else {
        this.#removeClaim({ identityKey, claimKey, groupId });
      }
      return result;
    } catch (error) {
      this.#removeClaim({ identityKey, claimKey, groupId });
      throw error;
    }
  }

  #readClaim({ identityKey, claimKey }) {
    const current = storage()
      ? readStoredClaim(claimKey)
      : this.memoryClaims.get(identityKey) || null;
    if (!current) return null;
    if (Number(current.expiresAt || 0) > Date.now()) return current;

    this.#removeClaim({ identityKey, claimKey });
    return null;
  }

  #writeClaim({ identityKey, claimKey, claim }) {
    if (storage()) {
      writeStoredClaim(claimKey, claim);
    } else {
      this.memoryClaims.set(identityKey, claim);
    }
  }

  #removeClaim({ identityKey, claimKey, groupId = null }) {
    if (storage()) {
      removeStoredClaim(claimKey, groupId);
      return;
    }
    const current = this.memoryClaims.get(identityKey);
    if (!groupId || current?.groupId === groupId) {
      this.memoryClaims.delete(identityKey);
    }
  }
}

export const welcomeRecoveryCoordinator = new WelcomeRecoveryCoordinator();
